import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarDays, MapPin, Building2, Target, ListChecks } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { localeOf, htmlLangOf } from "../i18n";
import { useSeo, SITE_BASE } from "../lib/useSeo";
import { ShareButtons } from "../components/ShareButtons";
import { Button } from "../components/ui/button";

/** Public, crawlable landing page for a VytaliDay — no login required. */
export default function PublicEvent() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [event, setEvent] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [meta, setMeta] = useState(null);
  const locale = localeOf(i18n.language);

  useEffect(() => {
    api.get(`/public/events/${id}`).then(({ data }) => setEvent(data)).catch(() => setNotFound(true));
    api.get(`/public/meta/events/${id}`, { params: { lang: i18n.language } })
      .then(({ data }) => setMeta(data)).catch(() => {});
  }, [id, i18n.language]);

  useSeo({
    title: meta?.title || (event ? `${event.title} · VytaliDays` : "VytaliDays"),
    description: meta?.description || event?.description || "",
    image: meta?.image,
    path: event ? `/events/${event.slug}` : undefined,
    type: "article",
    lang: htmlLangOf(i18n.language),
    jsonLd: meta?.jsonld,
  });

  if (notFound) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-display text-2xl font-bold">{t("pub.noEvents")}</h1>
          <Link to="/events" data-testid="public-event-back-link"
            className="mt-4 inline-block font-bold text-primary">{t("pub.allEvents")} →</Link>
        </div>
      </div>
    );
  }
  if (!event) return <p className="p-8 text-center text-sm text-muted-foreground">{t("common.loading")}</p>;

  const shareUrl = `${SITE_BASE}/events/${event.slug}`;

  return (
    <div data-testid="public-event-page">
      <section className="vy-hero px-4 py-12 sm:px-6 sm:py-16">
        <div className="mx-auto max-w-4xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent">
            {event.campaign_name || t("common.appName")}
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight text-white sm:text-5xl"
            data-testid="public-event-title">{event.title}</h1>
          <div className="mt-5 space-y-1.5 text-sm text-white/80">
            <p className="flex items-center gap-2" data-testid="public-event-date">
              <CalendarDays className="h-4 w-4" />{fmtDate(event.starts_at, locale)}
            </p>
            {event.location && (
              <p className="flex items-center gap-2" data-testid="public-event-location">
                <MapPin className="h-4 w-4" />{event.location}
              </p>
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl space-y-6 px-4 py-10 sm:px-6">
        {meta?.image && (
          <img src={meta.image} alt={event.title} width="1200" height="630"
            data-testid="public-event-image"
            className="aspect-[1200/630] w-full rounded-2xl border border-border object-cover" />
        )}
        {event.description && <p className="text-base text-muted-foreground">{event.description}</p>}

        {event.agenda && (
          <section className="rounded-2xl border border-border bg-card p-6">
            <p className="flex items-center gap-2 font-display text-base font-bold">
              <ListChecks className="h-4 w-4" />{t("events.agenda")}
            </p>
            <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">{event.agenda}</p>
          </section>
        )}
        {event.objectives && (
          <section className="rounded-2xl border border-border bg-card p-6">
            <p className="flex items-center gap-2 font-display text-base font-bold">
              <Target className="h-4 w-4" />{t("events.objectives")}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">{event.objectives}</p>
          </section>
        )}
        {event.partner && (
          <section className="rounded-2xl border border-border bg-card p-6" data-testid="public-event-partner">
            <p className="flex items-center gap-2 font-display text-base font-bold">
              <Building2 className="h-4 w-4" />{t("events.partner")}
            </p>
            <p className="mt-2 text-sm font-semibold">{event.partner.name}</p>
            {event.partner.address && (
              <p className="text-xs text-muted-foreground">{event.partner.address}</p>
            )}
          </section>
        )}

        <ShareButtons url={shareUrl} title={event.title} testPrefix="event-share" />

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button asChild className="h-14 flex-1 rounded-full text-base font-bold">
            <Link to="/login" data-testid="public-event-cta">{t("auth.signIn")}</Link>
          </Button>
          <Button asChild variant="outline" className="h-14 rounded-full px-7 font-bold">
            <Link to="/partners" data-testid="public-event-partner-cta">{t("pub.ctaPartner")}</Link>
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">{t("auth.inviteOnly")}</p>
      </div>
    </div>
  );
}
